import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  useMemo,
} from "react";
import PCalendar from "p-react-calendar";
import "p-react-calendar/dist/PCalendar.css";
import "./ReservationCalendar.css";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { PButtonPure, PSpinner } from "@porsche-design-system/components-react";
import BlurFade from "@/components/magicui/blur-fade";
import { IsRotatedContext } from "MainApp/assets/utils/context/isRotatedContext";
import { getIntlLocale } from "MainApp/assets/utils/js/localeUtils";
import { spacingFluidXSmall } from "@porsche-design-system/components-js/styles";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";
import CButton from "Components/CComponents/CButton/CButton";
import CText from "Components/CComponents/CText/CText";
import {
  getWeekdayText,
  shouldUseNarrowWeekdayLabels,
} from "./calendarWeekdayLabels";
import { calendarDateToBusinessEpochSeconds } from "./reservationDateUtils";

const ReservationCalendar = ({
  onDaySelect,
  onBack,
  isPhone,
  tourUUID,
  selectedDuration,
}) => {
  const { axiosInstance, userData, isTuk } = useContext(AuthContext);
  const { t, i18n } = useTranslation("common");
  const [disabledDates, setDisabledDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [currentView, setCurrentView] = useState("month");
  const [selectedDate, setSelectedDate] = useState(null);
  const previousActiveStartDate = useRef(activeStartDate);
  const calendarWrapperRef = useRef(null);
  const [isRetrotourIframe, setIsRetrotourIframe] = useState(false);
  const [calendarWidth, setCalendarWidth] = useState(0);

  useEffect(() => {
    try {
      setIsRetrotourIframe(userData.data.companyName === "Retro Tour Madrid");
    } catch {
      setIsRetrotourIframe(false);
    }
  }, [userData]);

  useEffect(() => {
    const node = calendarWrapperRef.current;
    if (!node) return undefined;

    const updateCalendarWidth = () => {
      setCalendarWidth(Math.round(node.getBoundingClientRect().width));
    };

    updateCalendarWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateCalendarWidth);
      return () => window.removeEventListener("resize", updateCalendarWidth);
    }

    const observer = new ResizeObserver(updateCalendarWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, [loading]);

  const calendarLocale = getIntlLocale(i18n.language);
  const shouldUseNarrowWeekdays = useMemo(() => {
    const viewportWidth =
      typeof window !== "undefined" ? Math.round(window.innerWidth) : 0;

    return shouldUseNarrowWeekdayLabels({
      isPhone,
      locale: calendarLocale,
      i18nLanguage: i18n.language,
      calendarWidth,
      viewportWidth,
    });
  }, [calendarLocale, calendarWidth, i18n.language, isPhone]);

  const formatCalendarWeekday = useCallback(
    (locale, date) =>
      shouldUseNarrowWeekdays
        ? getWeekdayText(locale, date, "narrow")
        : getWeekdayText(locale, date, "short"),
    [shouldUseNarrowWeekdays]
  );

  const fetchDisabledDates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/toursAPI/${tourUUID}/get-unavailable-days`, {
        params: {
          duration: Number(selectedDuration || 0) || undefined,
        },
      });
      setDisabledDates(response.data);
    } catch (error) {
      console.error("Error fetching disabled dates", error);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, tourUUID, selectedDuration]);

  useEffect(() => {
    fetchDisabledDates();
    previousActiveStartDate.current = activeStartDate;
  }, [activeStartDate, fetchDisabledDates]);

  const tileDisabled = ({ date, view }) => {
    if (view === "month") {
      const dateString = DateTime.fromJSDate(date).toFormat("yyyy-MM-dd");
      const isPastDate = date < new Date().setHours(0, 0, 0, 0);
      return disabledDates.includes(dateString) || isPastDate;
    }
    return false;
  };

  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      const classes = [];
      // leading/trailing
      const year = activeStartDate.getFullYear();
      const month = activeStartDate.getMonth();
      if (date.getFullYear() !== year || date.getMonth() !== month) {
        classes.push(
          date < activeStartDate
            ? "react-calendar__tile--leading"
            : "react-calendar__tile--trailing"
        );
      }
      // selected
      if (
        selectedDate &&
        date.getFullYear() === selectedDate.getFullYear() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getDate() === selectedDate.getDate()
      ) {
        classes.push("react-calendar__tile--selected");
      }
      return classes.join(" ") || null;
    }
    return null;
  };

  const handleActiveStartDateChange = ({ activeStartDate, view }) => {
    if (currentView !== view) setCurrentView(view);
    if (
      (previousActiveStartDate.current.getMonth() !==
        activeStartDate.getMonth() ||
        previousActiveStartDate.current.getFullYear() !==
          activeStartDate.getFullYear()) &&
      currentView === view
    ) {
      setActiveStartDate(activeStartDate);
      previousActiveStartDate.current = activeStartDate;
    }
  };

  const handleClickDay = (clickedDate) => {
    if (clickedDate.getMonth() !== activeStartDate.getMonth()) {
      const newActiveStartDate = new Date(
        clickedDate.getFullYear(),
        clickedDate.getMonth(),
        1
      );
      setActiveStartDate(newActiveStartDate);
    } else {
      setSelectedDate(clickedDate);
    }
  };

  const handleContinue = () => {
    if (selectedDate) {
      const epochTime = calendarDateToBusinessEpochSeconds(selectedDate);
      onDaySelect(epochTime);
    }
  };

  const goBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }

    window.history.back();
  };

  const { isRotated } = useContext(IsRotatedContext);
  const rotatedStyle = { height: isRotated ? "100svw" : "100svh" };

  return (
    <BlurFade className="h-full relative w-full">
      {!loading && (
      // {!loading && !isPhone && (
        <PButtonPure
          className="back-button-calendar"
          icon="arrow-left"
          hideLabel={true}
          tabindex={-1}
          onClick={goBack}
          style={{ marginLeft: spacingFluidXSmall }}
        />
      )}
      {loading ? (
        <div className="spinnerContainer">
          <PSpinner className="spinnerCalendar" />
          <CText>{t("checkingAvailability")}</CText>
        </div>
      ) : (
        <BlurFade
          className="flex w-[99%] justify-center relative"
          style={rotatedStyle}
        >
          <div
            ref={calendarWrapperRef}
            className={`calendar-wrapper ${
              isRetrotourIframe ? "calendar-wrapper--retrotour" : ""
            }`}
          >
            <PCalendar
              className={[
                isPhone ? "react-calendar-mobile" : "",
                shouldUseNarrowWeekdays
                  ? "react-calendar-mobile--narrow-weekdays"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              locale={calendarLocale}
              formatShortWeekday={formatCalendarWeekday}
              showNeighboringMonth
              showFixedNumberOfWeeks
              tileDisabled={tileDisabled}
              tileClassName={tileClassName}
              onActiveStartDateChange={handleActiveStartDateChange}
              activeStartDate={activeStartDate}
              onViewChange={({ activeStartDate, view }) => {
                setCurrentView(view);
                setActiveStartDate(activeStartDate);
                if (
                  activeStartDate.getFullYear() === new Date().getFullYear() &&
                  activeStartDate.getMonth() === new Date().getMonth()
                ) {
                  setSelectedDate(new Date());
                }
              }}
              onClickDay={handleClickDay}
            />
            {selectedDate && (
              <div className="reservation-calendar-actions">
                <CButton
                  className={`continue-button ${
                    isTuk ? "continue-button--tuk" : ""
                  }`}
                  onClick={handleContinue}
                >
                  {t("continueButton", "Continuar")}
                </CButton>
              </div>
            )}
          </div>
        </BlurFade>
      )}
    </BlurFade>
  );
};

export default ReservationCalendar;
